import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  HairlineCard,
  Icons,
  iconSize,
  Link,
  palette,
  Stack,
  StatusChip,
  Step,
  StepLabel,
  Stepper,
  Tooltip,
  Typography,
  fontStacks,
} from '@munifocus/ui';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  isConfiguredBylawCategory,
  type EscalationStageData,
  type Step5Data,
} from '@munifocus/contracts-gov';
import { AppShell } from '../components/AppShell';
import { DocumentCatalog } from '../components/DocumentCatalog';
import { ClerkApprovalsPanel } from '../components/clerk/ClerkApprovalsPanel';
import { FileAttachments } from '../components/FileAttachments';
import { PhotoTimelinePanel } from '../components/evidence/PhotoTimelinePanel';
import { FileEmergencyBanner } from '../components/FileEmergencyBanner';
import { WarrantLog } from '../components/WarrantLog';
import { CitizenAccessPanel } from '../components/CitizenAccessPanel';
import { DocumentRequestsPanel } from '../components/DocumentRequestsPanel';
import { FileTeamPanel } from '../components/administration/FileTeamPanel';
import { IntakeSummary } from '../components/IntakeSummary';
import { FileActivity } from '../components/FileActivity';
import { FileDiscussionPanel } from '../components/chat/FileDiscussionPanel';
import { VisitTimeline } from '../components/VisitTimeline';
import { PropertyHistoryPanel } from '../components/PropertyHistoryPanel';
import { ContactHistoryPanel } from '../components/front-desk/ContactHistoryPanel';
import { FileTags } from '../components/FileTags';
import { useRecordRecentFile } from '../lib/recent-files';
import { FileNotesPanel } from '../components/teamwork/FileNotesPanel';
import { FollowFileButton } from '../components/teamwork/FollowFileButton';
import { FileHandoversCard } from '../components/teamwork/FileHandoversCard';
import { TicketsPanel } from '../components/TicketsPanel';
import { CostRecoverySummaryCard } from '../components/cost-recovery/CostRecoverySummaryCard';
import { AppealsPanel } from '../components/appeals/AppealsPanel';
import { CourtPackagePanel } from '../components/CourtPackagePanel';
import { useAppeals } from '../lib/appeals-api';
import { CaseAssistantPanel } from '../components/assistant/CaseAssistantPanel';
import { SafetyTimerPanel } from '../components/safety/SafetyTimerPanel';
import { HazardPanel } from '../components/safety/HazardPanel';
import { ReinspectionPanel } from '../components/reinspection/ReinspectionPanel';
import { DirectionsLink, HazardBanner } from '../components/safety/HazardWarning';
import { useHazards } from '../lib/safety-api';
import { useSessionAccess } from '../lib/administration-api';
import { useAuth } from '../providers/AuthProvider';
import { useFile } from '../lib/files-api';
import { useFileWorkflow } from '../lib/bylaw-workflows-api';
import { googleDirectionsUrl } from '../lib/directions';
import {
  DU_WORKFLOW_REF,
  WORKABLE_STAGES,
  stageLegislativeRef,
  countyLabel,
  localizeStatuteRef,
  withSpecify,
  stageLabelKey,
  statusChipKind,
  statusLabelKey,
} from '../lib/file-display';
import {
  ESCALATION_STEPS,
  ESCALATION_STEP_REF,
  escalationStepLabelKey,
  escalationStepSlug,
} from '../lib/escalation-display';
import { FileLoadError } from '../components/FileLoadError';

function Field({ label, value }: { label: string; value: string | null }) {
  return (
    <Stack direction="row" sx={{ justifyContent: 'space-between', gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ textAlign: 'right' }}>
        {value && value.trim() !== '' ? value : '—'}
      </Typography>
    </Stack>
  );
}

/** Sections the palette and the shortcuts can land on (`?focus=`). */
const FOCUSABLE_SECTIONS = ['documents', 'visits', 'team', 'history'] as const;

function fileSectionId(section: (typeof FOCUSABLE_SECTIONS)[number]): string {
  return `file-section-${section}`;
}

/**
 * Bring a `?focus=` section into view and move focus to it once the file has
 * rendered, so "Generate document" from the palette leaves the officer at the
 * catalogue — and a screen reader announces where they landed.
 */
function useFocusSection(ready: boolean) {
  const [params] = useSearchParams();
  const focus = params.get('focus');
  useEffect(() => {
    if (!ready || !focus) return;
    if (!(FOCUSABLE_SECTIONS as readonly string[]).includes(focus)) return;
    const el = document.getElementById(`file-section-${focus}`);
    if (!el) return;
    el.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    el.focus({ preventScroll: true });
  }, [ready, focus]);
}

export function FileDetail() {
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: file, isLoading, isError, error } = useFile(id);
  useRecordRecentFile(file);
  useFocusSection(!!file);
  const location = useLocation();

  // A mention in the file discussion links here with `?panel=discussion`.
  useEffect(() => {
    if (!file || new URLSearchParams(location.search).get('panel') !== 'discussion') return;
    document.getElementById('file-discussion')?.scrollIntoView?.({ behavior: 'smooth' });
  }, [file, location.search]);
  // Whether the court & appeals module is on at this file's municipality. Off,
  // both panels render nothing and the catalog leaves out the committee report.
  // Asked once the file has loaded: for a file the officer cannot open the
  // answer is a 404 that says nothing the file's own 404 has not.
  // Not asked at all where the detail says appeals are off here.
  const appealsEnabled = Boolean(
    useAppeals(file && file.modules?.appeals !== false ? id : '').data?.enabled,
  );
  // A configured by-law type cites its own by-law, resolved for the file's
  // municipality; the D&U files keep the Local Governance Act reference.
  const configured = isConfiguredBylawCategory(file?.bylawCategory);
  const workflow = useFileWorkflow(id, configured);
  const mainStatute = workflow.data?.definition.statutes[0]?.key;
  const mainCitation = mainStatute ? (workflow.data?.definition.citations[mainStatute] ?? '') : '';
  const { user } = useAuth();
  // Asked at this file: an officer holds file:update through their seat on it.
  const fileAccess = useSessionAccess('file', id, user?.id);
  const canUpdate = fileAccess.data?.permissions.includes('file:update') ?? false;
  const canAddEvidence = fileAccess.data?.permissions.includes('evidence:add') ?? false;
  // A read-only seat (a non-subscribing municipality's Clerk) reads documents,
  // attachments and who has portal access, and is offered nothing it would be
  // refused.
  const canDraft = fileAccess.data?.permissions.includes('document:draft') ?? false;
  const canGrantCitizen = fileAccess.data?.permissions.includes('citizen:grant') ?? false;
  const canRevokeCitizen = fileAccess.data?.permissions.includes('citizen:revoke') ?? false;
  const hazards = useHazards(file ? id : undefined).data?.hazards ?? [];

  const activeStep = file ? WORKABLE_STAGES.indexOf(file.stage) : -1;

  const escalation = (file?.stageData?.escalation ?? {}) as EscalationStageData;
  const step5 = (file?.stageData?.step_5 ?? {}) as Step5Data;
  const showSubflow = file?.stage === 'escalation' && Boolean(escalation.escalationType);
  const step5Served = Boolean(step5.serviceMethod && step5.serviceDate);
  const directions = file ? googleDirectionsUrl(file) : null;

  return (
    <AppShell
      // The file is one level down from the queue and hides the tab bar while
      // it has a stage to continue, so this is its only way back up.
      back={{ label: t('gov.nav.files'), onClick: () => navigate('/') }}
      // Names the act as well as the record: "Viewing file F-2026-0004 · 88 Rue
      // St-Antoine" sits next to an "Edit file" screen titled the same way, so
      // the bar always says which of the two you are on — and the reference is
      // how officers refer to a file to each other.
      title={
        file
          ? t('gov.file.viewTitle', { reference: file.reference, address: file.address })
          : undefined
      }
      // One edit screen, reached from the bar as well as from the cards below:
      // editing a record is a top-level thing to want, and hunting for a button
      // further down assumes the officer knows it is there.
      // Both hidden from a read-only seat (a non-subscribing municipality's
      // Clerk, a councillor-observer): the API would refuse the edit and every
      // stage save behind Continue.
      actions={
        file && canUpdate ? (
          <Button
            size="small"
            startIcon={<Icons.Pencil size={iconSize.md} />}
            onClick={() => navigate(`/files/${file.id}/edit`)}
          >
            {t('gov.file.edit.action')}
          </Button>
        ) : undefined
      }
      bottomBar={
        file && canUpdate && file.stage !== 'closed' ? (
          <Button
            fullWidth
            variant="contained"
            onClick={() => navigate(`/files/${file.id}/stage/${file.stage}`)}
          >
            {t('gov.file.continueStage', { stage: t(stageLabelKey(file.stage)) })}
          </Button>
        ) : undefined
      }
    >
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {isError && <FileLoadError error={error} />}

      {file && (
        <>
          {/* Sticky-ish file context: reference + address always at the top. */}
          <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h5" sx={{ fontFamily: fontStacks.mono }}>
              {file.reference}
            </Typography>
            <Stack direction="row" spacing={0.5}>
              {file.emergency && (
                <StatusChip status="emergency" label={t('gov.status.emergency')} />
              )}
              <StatusChip
                status={statusChipKind(file.status)}
                label={t(statusLabelKey(file.status))}
              />
            </Stack>
          </Stack>
          <Stack
            direction="row"
            sx={{ alignItems: 'baseline', justifyContent: 'space-between', gap: 2, mb: 2 }}
          >
            <Stack direction="row" spacing={1.5} sx={{ alignItems: 'baseline', flexWrap: 'wrap' }}>
              <Typography variant="body2" color="text.secondary">
                {file.address}
              </Typography>
              {directions && (
                <DirectionsLink href={directions} address={file.address} hazards={hazards} />
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              {configured
                ? mainCitation || t('gov.bylawWorkflow.citationNotConfigured')
                : localizeStatuteRef(DU_WORKFLOW_REF)}
            </Typography>
          </Stack>

          {/* Before anything else on the file: who might be hurt going there. */}
          <HazardBanner hazards={hazards} />
          <Stack direction="row" sx={{ mb: 2, gap: 1, flexWrap: 'wrap' }}>
            <FollowFileButton fileId={file.id} />
          </Stack>

          <FileEmergencyBanner emergency={file.emergency} />

          {file.tags && file.tags.length > 0 && <FileTags tags={file.tags} sx={{ mb: 2 }} />}

          <HairlineCard sx={{ p: 2 }}>
            <Typography variant="overline">{t('gov.file.stage')}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {t('gov.file.stageHint')}
            </Typography>
            <Stepper activeStep={activeStep} orientation="vertical" sx={{ mt: 1 }}>
              {WORKABLE_STAGES.map((s) => (
                <Step key={s} completed={WORKABLE_STAGES.indexOf(s) < activeStep}>
                  {/* These rows have always been links; nothing said so. Now the
                      row lifts on hover, names what it opens, and answers to the
                      keyboard — a stage you reach only by clicking something that
                      looked inert is a stage nobody reaches on purpose.

                      The control is a real `<button>` rather than a `<span>`
                      carrying `role="button"`: `StepLabel` renders a span, and
                      re-implementing Enter/Space by hand is a worse answer than
                      the element that already has the semantics (WCAG 2.1.1). */}
                  <Tooltip
                    title={t('gov.file.openStage', { stage: t(stageLabelKey(s)) })}
                    // The row spans the whole card, so anchoring to it put the
                    // label a card's width away from the thing being pointed at.
                    // Follow the cursor instead: it belongs where the eye is.
                    followCursor
                    placement="top-start"
                    enterDelay={300}
                  >
                    <StepLabel slotProps={{ label: { sx: { width: '100%' } } }}>
                      <Box
                        component="button"
                        type="button"
                        aria-label={t('gov.file.openStage', { stage: t(stageLabelKey(s)) })}
                        onClick={() => navigate(`/files/${file.id}/stage/${s}`)}
                        sx={{
                          display: 'flex',
                          width: '100%',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 2,
                          appearance: 'none',
                          border: 'none',
                          background: 'none',
                          font: 'inherit',
                          color: 'inherit',
                          textAlign: 'left',
                          cursor: 'pointer',
                          borderRadius: 1.5,
                          px: 1,
                          mx: -1,
                          transition: 'background-color 200ms',
                          '&:hover': { backgroundColor: palette.brand.accentTint },
                          // The chevron is the affordance; it only earns its space
                          // while the row is actually being pointed at.
                          '&:hover .stage-open, &:focus-visible .stage-open': { opacity: 1 },
                        }}
                      >
                        <span>{t(stageLabelKey(s))}</span>
                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                          {stageLegislativeRef(file.bylawCategory, s) && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ whiteSpace: 'nowrap' }}
                            >
                              {localizeStatuteRef(stageLegislativeRef(file.bylawCategory, s))}
                            </Typography>
                          )}
                          <Box
                            className="stage-open"
                            sx={{
                              display: 'flex',
                              opacity: 0,
                              transition: 'opacity 200ms',
                              color: palette.brand.accent,
                            }}
                          >
                            <Icons.ChevronRight size={iconSize.md} />
                          </Box>
                        </Stack>
                      </Box>
                    </StepLabel>
                  </Tooltip>
                </Step>
              ))}
            </Stepper>
          </HairlineCard>

          {showSubflow && (
            <HairlineCard sx={{ p: 2, mt: 2 }}>
              <Typography variant="overline">{t('gov.escalation.subflowTitle')}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t('gov.escalation.type3')}
              </Typography>
              <Stack divider={<Divider flexItem />}>
                {ESCALATION_STEPS.map((s) => (
                  // Was a `div` with an `onClick`: Steps 5-9 of the escalation
                  // sub-flow could not be opened from the keyboard at all.
                  <Stack
                    key={s}
                    component="button"
                    type="button"
                    direction="row"
                    onClick={() =>
                      navigate(`/files/${file.id}/escalation/${escalationStepSlug(s)}`)
                    }
                    sx={{
                      cursor: 'pointer',
                      py: 1,
                      width: '100%',
                      appearance: 'none',
                      border: 'none',
                      background: 'none',
                      font: 'inherit',
                      color: 'inherit',
                      textAlign: 'left',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 2,
                    }}
                  >
                    <Typography variant="body2">{t(escalationStepLabelKey(s))}</Typography>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                      {s === 'step_5' && step5Served && (
                        <StatusChip status="success" label={t('gov.escalation.step5.served')} />
                      )}
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ whiteSpace: 'nowrap' }}
                      >
                        {localizeStatuteRef(ESCALATION_STEP_REF[s])}
                      </Typography>
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            </HairlineCard>
          )}

          <HairlineCard sx={{ p: 2, mt: 2 }}>
            <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="overline">{t('gov.file.details')}</Typography>
              <Stack direction="row" spacing={0.5} sx={{ display: canUpdate ? 'flex' : 'none' }}>
                {/* Two different acts: correcting a value, and re-reading the
                    property off an SNB record (which logs what it changed).
                    Hidden from a read-only seat, which the API would refuse. */}
                <Button
                  size="small"
                  startIcon={<Icons.FileText size={iconSize.sm} />}
                  onClick={() => navigate(`/files/${file.id}/tombstone`)}
                >
                  {t('gov.extraction.tombstone.action')}
                </Button>
                <Button
                  size="small"
                  startIcon={<Icons.Pencil size={iconSize.sm} />}
                  onClick={() => navigate(`/files/${file.id}/edit`)}
                >
                  {t('common.actions.edit')}
                </Button>
              </Stack>
            </Stack>
            <Stack spacing={1} sx={{ mt: 1 }} divider={<Divider flexItem />}>
              <Field
                label={t('gov.file.bylawCategory')}
                value={withSpecify(t(`gov.bylaw.${file.bylawCategory}`), file.bylawCategoryOther)}
              />
              <Field
                label={t('gov.file.priority')}
                // §F has three levels, and their labels live under `gov.priority`.
                // The old two-way urgent/normal guess pointed at keys that no
                // longer exist, so the row printed the key itself.
                value={t(`gov.priority.${file.priority}`)}
              />
              <Field label={t('gov.file.pan')} value={file.pan} />
              <Field label={t('gov.file.county')} value={countyLabel(file.county, t)} />
              <Field
                label={t('gov.file.pids')}
                value={
                  file.parcels?.length ? file.parcels.map((p) => p.pid).join('; ') : file.propertyId
                }
              />
              <Field
                label={t('gov.file.ownerName')}
                value={file.ownerUnknown ? t('gov.file.ownerUnknown') : file.ownerName}
              />
              <Field
                label={t('gov.file.ownerType')}
                value={
                  file.ownerType
                    ? withSpecify(t(`gov.ownerType.${file.ownerType}`), file.ownerTypeOther)
                    : null
                }
              />
              <Field label={t('gov.file.mailingAddress')} value={file.mailingAddress} />
              <Field label={t('gov.file.notes')} value={file.notes} />
            </Stack>
          </HairlineCard>

          {file.complaintId && (
            <HairlineCard sx={{ p: 2, mt: 2 }}>
              <IntakeSummary fileId={file.id} complaintId={file.complaintId} canEdit={canUpdate} />
            </HairlineCard>
          )}

          {/* Renders nothing unless the caller holds ai:assist here and the
              municipality turned the assistant on. */}
          <CaseAssistantPanel fileId={file.id} />

          <HairlineCard id={fileSectionId('documents')} tabIndex={-1} sx={{ p: 2, mt: 2 }}>
            <DocumentCatalog
              fileId={file.id}
              stage={file.stage}
              appealReport={appealsEnabled}
              canGenerate={canDraft}
            />
          </HairlineCard>

          {/* Form 4 name approval and council authorizations: what the Clerk
              was asked on this file, and what they answered. */}
          <ClerkApprovalsPanel fileId={file.id} />

          <HairlineCard sx={{ p: 2, mt: 2 }}>
            <FileAttachments fileId={file.id} canAttach={canDraft} />
          </HairlineCard>

          {/* Nothing at all unless the municipality has evidence markup on. */}
          <PhotoTimelinePanel fileId={file.id} canEdit={canAddEvidence} />

          {/* Nothing at all unless the municipality has cost recovery on and
              this account may read it. */}
          <CostRecoverySummaryCard
            fileId={file.id}
            enabled={file.modules?.costRecovery !== false}
          />

          <HairlineCard sx={{ p: 2, mt: 2 }}>
            <HazardPanel fileId={file.id} canEdit={canUpdate} />
          </HairlineCard>

          <HairlineCard id={fileSectionId('visits')} tabIndex={-1} sx={{ p: 2, mt: 2 }}>
            <VisitTimeline fileId={file.id} />
            {canUpdate && (
              <Box sx={{ mt: 2 }}>
                <SafetyTimerPanel fileId={file.id} localGovernmentId={file.localGovernmentId} />
              </Box>
            )}
          </HairlineCard>

          {/* The re-inspection the owner books. Draws its own card, and nothing
              at all where the municipality has booking off. */}
          <ReinspectionPanel fileId={file.id} canEdit={canUpdate} />

          {/* Beside the visits: what happened at this property before is the
              context an officer reads before going back to it. */}
          <HairlineCard id={fileSectionId('history')} tabIndex={-1} sx={{ p: 2, mt: 2 }}>
            <PropertyHistoryPanel fileId={file.id} />
            <ContactHistoryPanel fileId={file.id} />
          </HairlineCard>

          <HairlineCard sx={{ p: 2, mt: 2 }}>
            <WarrantLog fileId={file.id} />
          </HairlineCard>

          {/* Each renders nothing unless the municipality has the module. */}
          {file.modules?.appeals !== false && <AppealsPanel fileId={file.id} />}
          <CourtPackagePanel fileId={file.id} />

          {/* Draws its own card, and nothing at all where tickets are off. */}
          <TicketsPanel
            fileId={file.id}
            mayIssue={fileAccess.data?.permissions.includes('ticket:issue') ?? false}
          />

          {/* Who works this file, above who outside it can see it: the team is
              the access decision an officer makes daily, the citizen grant is
              the one they make occasionally. */}
          <Box id={fileSectionId('team')} tabIndex={-1} sx={{ mt: 2, outline: 'none' }}>
            <FileTeamPanel fileId={file.id} localGovernmentId={file.localGovernmentId} />
          </Box>

          <HairlineCard sx={{ p: 2, mt: 2 }}>
            <CitizenAccessPanel
              fileId={file.id}
              canGrant={canGrantCitizen}
              canRevoke={canRevokeCitizen}
            />

            {/* Directly under the access panel: who can see the file, and what
                they have asked it for, are the same question asked twice. */}
            <DocumentRequestsPanel fileId={file.id} />
          </HairlineCard>

          <HairlineCard sx={{ p: 2, mt: 2 }}>
            <FileNotesPanel fileId={file.id} />
          </HairlineCard>

          <HairlineCard sx={{ p: 2, mt: 2 }}>
            <FileHandoversCard file={file} />
          </HairlineCard>

          <HairlineCard sx={{ p: 2, mt: 2 }} id="file-discussion">
            <FileDiscussionPanel fileId={file.id} />
          </HairlineCard>

          {/* Last on the page on purpose: the trail is the record of everything
              above it, and it is read after the fact rather than worked from. */}
          <HairlineCard sx={{ p: 2, mt: 2 }}>
            <FileActivity fileId={file.id} />
          </HairlineCard>
        </>
      )}
    </AppShell>
  );
}
